import React from 'react'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'
import { InlineCode } from '../shared/InlineCode'

const Navigation = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <DocSection id='top-nav' title={t`Top Navigation Bar`}>
          <Paragraph size='$5'>
            <Trans>
              The navigation bar at the top of every page contains the following
              elements, from left to right:
            </Trans>
          </Paragraph>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='center'>
              <Icon name='guild-logo' size='$5' color='$color' flexShrink={0} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Guild logo
                  </Paragraph>{' '}
                  — Click the logo to return to the home page at any time.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='center'>
              <Icon name='people' size='$5' color='$color' flexShrink={0} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Guilds
                  </Paragraph>{' '}
                  — Browse the directory of communities on the platform. See{' '}
                  <Link href='/docs/guilds/overview'>Guilds</Link> to learn
                  more.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='center'>
              <Icon name='calendar' size='$5' color='$color' flexShrink={0} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Events
                  </Paragraph>{' '}
                  — Browse upcoming and past Events across all Guilds. See{' '}
                  <Link href='/docs/events/overview'>Events</Link> to learn
                  more.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='center'>
              <Icon
                name='projection-screen'
                size='$5'
                color='$color'
                flexShrink={0}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Presentations
                  </Paragraph>{' '}
                  — Browse talks and content shared across the platform. See{' '}
                  <Link href='/docs/presentations/overview'>Presentations</Link>{' '}
                  to learn more.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='center'>
              <Icon name='search' size='$5' color='$color' flexShrink={0} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Command Palette
                  </Paragraph>{' '}
                  — Open the{' '}
                  <Link href='/docs/getting-started/command-menu'>
                    Command Palette
                  </Link>{' '}
                  to search, create Guilds, Events, and Presentations, or access
                  contextual actions.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='center'>
              <Icon name='person' size='$5' color='$color' flexShrink={0} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Profile
                  </Paragraph>{' '}
                  — Access your profile, account settings, and sign in or sign
                  out.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
          <Paragraph size='$5'>
            <Trans>
              On smaller screens, the navigation links collapse into a hamburger
              menu. Tap the menu icon to expand it and access Guilds, Events,
              and Presentations.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='url-patterns' title={t`URL Patterns`}>
          <Paragraph size='$5'>
            <Trans>
              Guild uses clean, readable URLs that make it easy to share links
              to specific pages:
            </Trans>
          </Paragraph>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='people'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Guilds
                  </Paragraph>{' '}
                  — Accessed at <InlineCode>{'/:guildSlug'}</InlineCode>, for
                  example <InlineCode>/metro-coders</InlineCode>. Each Guild has
                  a unique slug that serves as its URL.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='calendar'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Events
                  </Paragraph>{' '}
                  — Accessed at <InlineCode>{'/events/:eventSlug'}</InlineCode>,
                  for example <InlineCode>/events/react-conf-2026</InlineCode>.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='projection-screen'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Presentations
                  </Paragraph>{' '}
                  — Accessed at{' '}
                  <InlineCode>{'/presentations/:presentationSlug'}</InlineCode>,
                  for example{' '}
                  <InlineCode>/presentations/intro-to-typescript</InlineCode>.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='person'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    User profiles
                  </Paragraph>{' '}
                  — Accessed at <InlineCode>{'/:userSlug'}</InlineCode>, sharing
                  the same top-level pattern as Guilds. Guild determines whether
                  a slug belongs to a Guild or a User automatically.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='guild-tabs' title={t`Guild Page Tabs`}>
          <Paragraph size='$5'>
            <Trans>
              When you visit a Guild page, you will see a set of tabs below the
              Guild&apos;s profile header. These tabs organize the Guild&apos;s
              content:
            </Trans>
          </Paragraph>
          <DocScreenshot
            src='docs/guilds/guild-profile-page.png'
            alt='Guild profile page showing tab navigation with Guild, Events, Presentations, and Members tabs below the profile header'
            caption={t`Guild page tabs organize content into Guild, Events, Presentations, and Members`}
          />
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='home'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Guild
                  </Paragraph>{' '}
                  — The home tab showing the Guild&apos;s description,
                  highlights, and recent activity.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='calendar'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Events
                  </Paragraph>{' '}
                  — A list of upcoming and past Events hosted by the Guild.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='projection-screen'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Presentations
                  </Paragraph>{' '}
                  — A library of talks and content shared within the Guild.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='people'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Members
                  </Paragraph>{' '}
                  — The roster of people who belong to the Guild, including
                  their roles.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
          <Paragraph size='$5'>
            <Trans>
              If you are an Organizer or Editor of the Guild, you will also see
              a Settings option that lets you manage the Guild&apos;s profile,
              Members, and integrations.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='mobile-navigation' title={t`Mobile Navigation`}>
          <Paragraph size='$5'>
            <Trans>
              On smaller screens, Guild adapts its layout for touch-friendly
              navigation. The top navigation links (Guilds, Events,
              Presentations) collapse into a hamburger menu icon. Tap it to
              expand the full navigation panel. The{' '}
              <Link href='/docs/getting-started/command-menu'>
                Command Palette
              </Link>{' '}
              and profile icon remain visible in the navigation bar for quick
              access on any screen size.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Navigation
